/* VSS Navigation 2.0 / 1.3.2. No dependencies; no changes to Enterprise prototypes. */
(() => {
  if (customElements.get('vss-mega-menu')) return;

  const menuBridges = new WeakMap();
  function attachMenuBridge(menu) {
    if (!menu) return;
    const existing = menuBridges.get(menu);
    if (existing) { existing.users += 1; return; }
    const onKeyDown = (event) => {
      // Internal levels handle Escape themselves; this covers the mobile root.
      if (event.key !== 'Escape' || event.defaultPrevented || event.target.closest('vss-mega-menu')) return;
      if (matchMedia('(min-width: 769px)').matches || !menu.mainContent?.classList.contains('is-open')) return;
      event.preventDefault();
      menu.closeMainMenu(false);
      menu.toggleOverlay(false);
      menu.mainToggle?.focus({ preventScroll: true });
    };
    menu.addEventListener('keydown', onKeyDown);
    menuBridges.set(menu, {users: 1, onKeyDown});
  }
  function detachMenuBridge(menu) {
    const bridge = menuBridges.get(menu);
    if (!bridge || --bridge.users > 0) return;
    menu.removeEventListener('keydown', bridge.onKeyDown);
    menuBridges.delete(menu);
  }

  class VSSMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.menu = this.closest('main-menu');
      this.disclosure = this.closest('.main-nav__item--dropdown');
      this.shell = this.closest('.vss-nav-shell');
      this.app = this.querySelector('[data-vss-app]');
      this.categories = Array.from(this.querySelectorAll('[data-vss-category]'));
      this.panels = Array.from(this.querySelectorAll('[data-vss-panel]'));
      this.availableCategories = this.categories.filter((category) => category.getAttribute('aria-disabled') !== 'true');
      this.defaultIndex = this.categories.indexOf(this.availableCategories[0]);
      // Keep the server-rendered links usable if the expected markup is incomplete.
      if (!this.app || !this.availableCategories.length || !this.disclosure || !this.shell || this.panels.length !== this.categories.length) return;

      this.desktop = matchMedia('(min-width: 769px)');
      this.hover = matchMedia('(hover: hover) and (pointer: fine)');
      this.delay = Math.min(500, Math.max(80, Number(this.dataset.hoverDelay) || 180));
      this.events = new AbortController();
      attachMenuBridge(this.menu);
      const options = { signal: this.events.signal };
      this.list = this.querySelector('[data-vss-category-scroll]');
      this.image = this.querySelector('img[data-vss-image]');
      this.graphic = this.querySelector('[data-vss-graphic]');
      this.previewTitle = this.querySelector('[data-vss-preview-title]');
      this.previewDescription = this.querySelector('[data-vss-preview-description]');
      this.textDecoder = document.createElement('textarea');
      this.previewLink = this.querySelector('[data-vss-preview-link]');
      this.activeIndex = this.defaultIndex;
      // Reconnecting an existing element keeps its image; do not suppress its refresh.
      this.currentImage = null;
      this.dataset.vssLevel = 'category';
      this.querySelector('[data-vss-fallback]').hidden = true;
      this.app.hidden = false;
      this.dataset.vssReady = '';

      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('focusin', (event) => this.onFocus(event), options);
      this.addEventListener('pointerover', (event) => this.onPointerOver(event), options);
      this.addEventListener('pointerout', (event) => this.onPointerOut(event), options);
      this.addEventListener('pointerleave', () => this.cancelHover(), options);
      this.addEventListener('keydown', (event) => this.onKeyDown(event), options);
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') event.stopPropagation();
      }, options);
      this.image.addEventListener('error', () => {
        this.image.hidden = true;
        this.graphic.hidden = false;
      }, options);
      this.image.addEventListener('load', () => {
        if (!this.currentImage || !this.image.complete || !this.image.naturalWidth) return;
        this.image.hidden = false;
        this.graphic.hidden = true;
      }, options);

      this.onBreakpoint = () => {
        this.cancelHover();
        this.dataset.vssLevel = 'category';
        this.activate(this.activeIndex);
        this.scheduleMeasure();
      };
      this.desktop.addEventListener('change', this.onBreakpoint);
      this.shell.addEventListener('transitionend', (event) => {
        if (event.target === this.shell) this.scheduleMeasure();
      }, options);
      window.addEventListener('resize', () => this.scheduleMeasure(), options);
      window.visualViewport?.addEventListener('resize', () => this.scheduleMeasure(), options);
      window.visualViewport?.addEventListener('scroll', () => this.scheduleMeasure(), options);
      this.observer = new MutationObserver(() => this.onDisclosureChange());
      this.observer.observe(this.disclosure, { attributes: true, attributeFilter: ['class'] });
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.closest('store-header') || this.shell);
      }
      this.activate(this.defaultIndex);
      this.onDisclosureChange();
    }

    disconnectedCallback() {
      this.cancelHover();
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.focusFrame);
      this.events?.abort();
      if (this.events) detachMenuBridge(this.menu);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.mobileThumbObserver?.disconnect();
      this.desktop?.removeEventListener('change', this.onBreakpoint);
      this.events = null;
      this.wasOpen = false;
    }

    isOpen() {
      return this.disclosure.classList.contains('is-open');
    }

    onDisclosureChange() {
      const open = this.isOpen();
      if (open && !this.wasOpen) {
        this.dataset.vssLevel = 'category';
        this.list.scrollTop = 0;
        this.activate(this.defaultIndex, true);
        // Enterprise opens with two animation frames. Run once it has a box.
        this.scheduleMeasure();
      }
      if (!open) {
        this.cancelHover();
        this.dataset.vssLevel = 'category';
        this.mobileThumbObserver?.disconnect();
      }
      this.inert = !open;
      this.wasOpen = open;
      if (open) this.scheduleMeasure();
    }

    scheduleMeasure() {
      if (!this.isOpen()) return;
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => {
        if (!this.isConnected || !this.isOpen()) return;
        const viewport = window.visualViewport;
        const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
        const top = Math.max(0, this.shell.getBoundingClientRect().top);
        const margin = this.desktop.matches ? 12 : 0;
        const height = Math.max(1, Math.floor(bottom - top - margin));
        this.style.setProperty('--vss-available', `${height}px`);
      });
    }

    cancelHover() {
      clearTimeout(this.hoverTimer);
      this.hoverTarget = null;
    }

    onPointerOver(event) {
      if (!this.desktop.matches || !this.hover.matches || event.pointerType === 'touch') return;
      const target = event.target.closest('[data-vss-category], [data-vss-sub]');
      if (!target || !this.contains(target) || target.getAttribute('aria-disabled') === 'true' || target.contains(event.relatedTarget)) return;
      this.cancelHover();
      // Hover must not remove a panel containing the keyboard user's focus.
      if (document.activeElement?.matches(':focus-visible') && (this.previewLink.contains(document.activeElement) || (target.hasAttribute('data-vss-category') && this.querySelector('[data-vss-center]').contains(document.activeElement)))) return;
      this.hoverTarget = target;
      this.hoverTimer = setTimeout(() => {
        if (!this.isOpen() || this.hoverTarget !== target) return;
        if (target.hasAttribute('data-vss-category')) {
          this.activate(Number(target.dataset.vssCategory));
        } else {
          this.showPreview(target);
        }
      }, this.delay);
    }

    onPointerOut(event) {
      const target = event.target.closest('[data-vss-category], [data-vss-sub]');
      if (target === this.hoverTarget && !target?.contains(event.relatedTarget)) this.cancelHover();
    }

    onFocus(event) {
      this.cancelHover();
      const category = event.target.closest('[data-vss-category]');
      if (category && this.desktop.matches) this.activate(Number(category.dataset.vssCategory));
      const subcategory = event.target.closest('[data-vss-sub]');
      if (subcategory && this.desktop.matches) this.showPreview(subcategory);
    }

    onClick(event) {
      if (event.target.closest('[data-vss-level-back]')) {
        event.preventDefault();
        event.stopPropagation();
        this.backToCategories();
        return;
      }
      if (event.target.closest('[data-vss-root-back]')) {
        event.preventDefault();
        event.stopPropagation();
        this.backToRoot();
        return;
      }
      if (event.target.closest('[data-vss-close]')) {
        event.preventDefault();
        event.stopPropagation();
        this.closeMenu();
        return;
      }
      const category = event.target.closest('[data-vss-category]');
      if (!category || category.tagName !== 'BUTTON') return; // Real links keep native click/new-tab behavior.
      event.preventDefault();
      event.stopPropagation();
      this.cancelHover();
      this.activate(Number(category.dataset.vssCategory));
      if (!this.desktop.matches) {
        this.dataset.vssLevel = 'subcategory';
        this.syncPanels();
        this.focusInComponent(this.panels[this.activeIndex].querySelector('[data-vss-level-back]'));
      } else {
        // A keyboard/touch activation can enter the panel without tabbing through every category.
        this.focusPanel();
      }
    }

    activate(index, forceReset = false) {
      if (!this.categories[index] || this.categories[index].getAttribute('aria-disabled') === 'true') return;
      const changed = index !== this.activeIndex;
      this.activeIndex = index;
      this.categories.forEach((category, i) => {
        category.toggleAttribute('data-vss-active', i === index);
      });
      this.syncPanels();
      if (changed || forceReset) {
        this.panels[index].querySelector('[data-vss-panel-scroll]').scrollTop = 0;
      }
      if (this.desktop.matches && this.isOpen()) this.showPreview(this.categories[index]);
    }

    syncPanels() {
      const leaf = this.categories[this.activeIndex].dataset.vssHasChildren !== 'true';
      this.toggleAttribute('data-vss-leaf', leaf);
      const panelVisible = this.desktop.matches ? !leaf : this.dataset.vssLevel === 'subcategory';
      this.panels.forEach((panel, index) => {
        const visible = panelVisible && index === this.activeIndex;
        panel.hidden = !visible;
        if (this.categories[index].tagName === 'BUTTON') {
          this.categories[index].setAttribute('aria-expanded', String(visible));
        }
      });
      this.syncMobileThumbnails();
    }

    syncMobileThumbnails() {
      this.mobileThumbObserver?.disconnect();
      this.mobileThumbObserver = null;
      if (this.desktop.matches || !this.isOpen()) return;
      const scroller = this.dataset.vssLevel === 'subcategory'
        ? this.panels[this.activeIndex].querySelector('[data-vss-panel-scroll]')
        : this.list;
      const rows = scroller.querySelectorAll('[data-vss-category]:not([aria-disabled="true"]), [data-vss-sub]');
      // Observe only the current level. Hidden panels never request thumbnail images.
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (this.mobileThumbObserver !== observer || this.desktop.matches || !this.isOpen()) return;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.loadMobileThumbnail(entry.target);
            observer.unobserve(entry.target);
          });
        }, { root: scroller, rootMargin: '48px 0px', threshold: 0 });
        this.mobileThumbObserver = observer;
      }
      rows.forEach((row) => {
        if (!row.querySelector('[data-vss-mobile-thumb]')) {
          const thumb = document.createElement('span');
          thumb.className = 'vss-nav__mobile-thumb';
          thumb.setAttribute('data-vss-mobile-thumb', '');
          thumb.setAttribute('aria-hidden', 'true');
          const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          icon.setAttribute('viewBox', '0 0 24 24');
          icon.setAttribute('fill', 'none');
          icon.setAttribute('stroke', 'currentColor');
          icon.setAttribute('stroke-width', '1.4');
          icon.setAttribute('focusable', 'false');
          [[4, 4], [14, 4], [4, 14], [14, 14]].forEach(([x, y]) => {
            const square = document.createElementNS(icon.namespaceURI, 'rect');
            Object.entries({x, y, width: 6, height: 6, rx: 1}).forEach(([key, value]) => square.setAttribute(key, value));
            icon.appendChild(square);
          });
          thumb.appendChild(icon);
          // Keep the original label first in the DOM; CSS places the thumbnail before it.
          row.appendChild(thumb);
          if (row.hasAttribute('data-vss-sub')) {
            const arrow = document.createElement('span');
            arrow.className = 'vss-nav__mobile-arrow';
            arrow.setAttribute('aria-hidden', 'true');
            arrow.textContent = '→';
            row.appendChild(arrow);
          }
          row.classList.add('vss-nav__mobile-row');
        }
        if (this.mobileThumbObserver) this.mobileThumbObserver.observe(row);
        else this.loadMobileThumbnail(row); // Native loading=lazy remains as a fallback.
      });
    }

    loadMobileThumbnail(row) {
      const thumb = row.querySelector('[data-vss-mobile-thumb]');
      if (!thumb || thumb.hasAttribute('data-vss-requested') || !row.dataset.vssImage) return;
      thumb.setAttribute('data-vss-requested', '');
      const image = document.createElement('img');
      image.alt = '';
      image.width = 48;
      image.height = 48;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.sizes = '48px';
      image.addEventListener('load', () => {
        thumb.setAttribute('data-vss-loaded', '');
      }, {once: true});
      image.addEventListener('error', () => image.remove(), {once: true});
      // These local listeners remain valid when the theme editor reconnects the same element.
      thumb.appendChild(image);
      if (row.dataset.vssSrcset) image.srcset = row.dataset.vssSrcset;
      image.src = row.dataset.vssImage;
    }

    showPreview(source) {
      if (!this.desktop.matches || !this.isOpen()) return;
      const data = source.dataset;
      // Decode description entities into plain text; never insert description HTML into the page.
      this.textDecoder.innerHTML = (data.vssDescription || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const description = this.textDecoder.value.replace(/\s+/g, ' ').trim();
      this.previewDescription.textContent = description;
      this.previewDescription.hidden = !description;
      // The guide card identifies the current category or focused subcategory.
      this.previewTitle.textContent = data.vssTitle || '';
      this.previewTitle.hidden = !data.vssTitle;
      this.previewLink.setAttribute('aria-label', `${this.previewLink.dataset.vssPreviewLabel || 'Explore collection'}: ${data.vssTitle || ''}`);
      this.previewLink.hidden = !data.vssUrl;
      if (data.vssUrl) {
        this.previewLink.setAttribute('href', data.vssUrl);
      } else {
        this.previewLink.removeAttribute('href');
      }
      this.image.alt = data.vssTitle || '';
      if (this.currentImage === (data.vssImage || '')) return;
      this.currentImage = data.vssImage || '';
      this.image.hidden = true;
      this.graphic.hidden = false;
      this.image.removeAttribute('srcset');
      this.image.removeAttribute('src');
      if (data.vssImage) {
        this.image.width = Number(data.vssImageWidth) || 640;
        this.image.height = Number(data.vssImageHeight) || 640;
        this.image.sizes = '260px';
        if (data.vssSrcset) this.image.srcset = data.vssSrcset;
        this.image.src = data.vssImage;
      }
    }

    focusInComponent(target) {
      if (!target) return;
      if (typeof trapFocus === 'function') trapFocus(this, target);
      else target.focus({ preventScroll: true });
    }

    focusPanel() {
      // Leaf categories have no center panel: ArrowRight enters the collection card.
      if (this.desktop.matches && this.hasAttribute('data-vss-leaf')) {
        const target = this.previewLink.hidden
          ? this.querySelector('[data-vss-preview] [data-vss-close]')
          : this.previewLink;
        // A taller preview may scroll; reveal the CTA when keyboard navigation enters it.
        target?.focus();
        return;
      }
      const panel = this.panels[this.activeIndex];
      const target = panel.querySelector('[data-vss-viewall]') || panel.querySelector('[data-vss-sub]') || panel.querySelector('[data-vss-panel-scroll]');
      target?.focus({ preventScroll: true });
    }

    backToCategories() {
      this.cancelHover();
      this.dataset.vssLevel = 'category';
      this.syncPanels();
      this.focusInComponent(this.categories[this.activeIndex]);
    }

    backToRoot() {
      const opener = this.disclosure.querySelector('.main-nav__toggle-btn');
      if (typeof this.menu?.close !== 'function') return;
      this.cancelHover();
      this.menu.opener = opener;
      this.menu.close(this.disclosure, false);
      this.menu.childNavOpen = false;
      this.menu.mainContent?.classList.remove('main-menu__content--no-focus');
      if (this.desktop.matches) {
        this.menu.toggleOverlay(false);
        opener?.focus({ preventScroll: true });
      } else if (typeof trapFocus === 'function') {
        trapFocus(this.menu, opener || this.menu.mainToggle);
      } else {
        opener?.focus({ preventScroll: true });
      }
    }

    closeMenu() {
      const desktop = this.desktop.matches;
      this.backToRoot();
      if (!desktop && typeof this.menu?.closeMainMenu === 'function') {
        this.menu.closeMainMenu(false);
        this.menu.toggleOverlay(false);
        this.menu.mainToggle?.focus({ preventScroll: true });
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (!this.desktop.matches && this.dataset.vssLevel === 'subcategory') this.backToCategories();
        else this.backToRoot();
        return;
      }
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const category = event.target.closest('[data-vss-category]');
      if (category) {
        const index = this.availableCategories.indexOf(category);
        let next = index;
        if (event.key === 'ArrowDown') next = (index + 1) % this.availableCategories.length;
        else if (event.key === 'ArrowUp') next = (index - 1 + this.availableCategories.length) % this.availableCategories.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.availableCategories.length - 1;
        else if (event.key === 'ArrowRight' && this.desktop.matches) {
          event.preventDefault();
          this.focusPanel();
          return;
        } else return;
        event.preventDefault();
        this.availableCategories[next].focus({ preventScroll: true });
        // Keep the focused row visible without moving any other scroll container.
        const row = this.availableCategories[next];
        const bounds = this.list.getBoundingClientRect();
        const rect = row.getBoundingClientRect();
        if (rect.top < bounds.top) this.list.scrollTop -= bounds.top - rect.top;
        if (rect.bottom > bounds.bottom) this.list.scrollTop += rect.bottom - bounds.bottom;
      } else if (event.key === 'ArrowLeft' && (event.target.closest('[data-vss-center]') ||
        (this.desktop.matches && this.hasAttribute('data-vss-leaf') && event.target.closest('[data-vss-preview]')))) {
        event.preventDefault();
        if (this.desktop.matches) this.categories[this.activeIndex].focus({ preventScroll: true });
        else this.backToCategories();
      }
    }
  }

  customElements.define('vss-mega-menu', VSSMegaMenu);
})();
